import React from 'react';
import Icon from '../../../components/AppIcon';

const NotificationCard = ({ notification, isSelected, onSelect, onMarkAsRead }) => {
  const getTypeIcon = (type) => {
    const icons = {
      quote_request: 'FileText',
      milestone_completed: 'CheckCircle',
      team_assignment: 'Users',
      payment_confirmation: 'CreditCard',
      inventory_alert: 'Package',
      schedule_reminder: 'Clock',
      assignment_notification: 'Calendar',
      urgent_alert: 'AlertTriangle',
    };
    return icons?.[type] || 'Bell';
  };

  const getTypeLabel = (type) => {
    const labels = {
      quote_request: 'Demande de Devis',
      milestone_completed: 'Jalon Complété',
      team_assignment: 'Assignation Équipe',
      payment_confirmation: 'Confirmation Paiement',
      inventory_alert: 'Alerte Stock',
      schedule_reminder: 'Rappel Mission',
      assignment_notification: 'Notification Assignation',
      urgent_alert: 'Alerte Urgente',
    };
    return labels?.[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      quote_request: 'text-blue-500',
      milestone_completed: 'text-success',
      team_assignment: 'text-purple-500',
      payment_confirmation: 'text-success',
      inventory_alert: 'text-warning',
      schedule_reminder: 'text-blue-500',
      assignment_notification: 'text-purple-500',
      urgent_alert: 'text-error',
    };
    return colors?.[type] || 'text-primary';
  };

  const getPriorityBadge = (priority, type) => {
    const isUrgent = priority === 'urgent' || type === 'urgent_alert';
    if (!isUrgent) return null;

    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-error/10 text-error flex items-center gap-1">
        <Icon name="AlertTriangle" size={12} />
        Urgent
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      read: 'bg-muted/50 text-muted-foreground',
      pending: 'bg-warning/10 text-warning',
      sent: 'bg-success/10 text-success',
      failed: 'bg-error/10 text-error',
    };
    const labels = {
      read: 'Lu',
      pending: 'En attente',
      sent: 'Envoyé',
      failed: 'Échec',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles?.[status] || 'bg-muted text-muted-foreground'}`}>
        {labels?.[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math?.floor(diffMs / 60000);
    const diffHours = Math?.floor(diffMs / 3600000);
    const diffDays = Math?.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isUnread = notification?.status !== 'read';

  return (
    <div
      className={`bg-card rounded-lg border transition-all duration-200 ${
        isUnread ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/30'
      } ${isSelected ? 'ring-2 ring-primary' : ''}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Checkbox */}
          <div className="flex items-center pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect?.(notification?.id)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
            />
          </div>

          {/* Icon */}
          <div className={`w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 ${getTypeColor(notification?.notificationType)}`}>
            <Icon name={getTypeIcon(notification?.notificationType)} size={20} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`font-semibold ${isUnread ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {notification?.subject}
                  </span>
                  {getPriorityBadge(notification?.priority, notification?.notificationType)}
                  {getStatusBadge(notification?.status)}
                </div>
                <p className="text-sm text-muted-foreground">
                  {getTypeLabel(notification?.notificationType)} • {formatDate(notification?.createdAt)}
                </p>
              </div>
            </div>
            <p className={`text-sm mb-3 ${isUnread ? 'text-foreground' : 'text-muted-foreground'}`}>
              {notification?.message}
            </p>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {isUnread && (
                <button
                  onClick={() => onMarkAsRead?.(notification?.id)}
                  className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm flex items-center gap-1.5"
                >
                  <Icon name="Check" size={14} />
                  Marquer comme lu
                </button>
              )}
              {notification?.referenceType && (
                <button className="px-3 py-1.5 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm flex items-center gap-1.5">
                  <Icon name="ExternalLink" size={14} />
                  Voir détails
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;